/**
 * Studios API client for Stage Door Access
 * Handles studio profile, contacts, dashboard, and bookings
 */

import type { ClassTimeSlot } from './events';
import type { BookingInquirySummary } from './inquiries';
import { handleUnauthorized } from './auth';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8787';

/**
 * Get the auth token from localStorage
 * Must match the storage key used by AuthContext
 */
function getAuthToken(): string | null {
  try {
    const tokensJson = localStorage.getItem('sda_auth_tokens');
    if (!tokensJson) return null;
    const tokens = JSON.parse(tokensJson) as { access_token: string; expires_at: number };
    // Check if token is expired
    if (tokens.expires_at * 1000 < Date.now()) {
      return null;
    }
    return tokens.access_token;
  } catch {
    return null;
  }
}

/**
 * Make an authenticated request
 */
async function authenticatedFetch(
  url: string,
  options: RequestInit = {}
): Promise<Response> {
  const token = getAuthToken();
  if (!token) {
    throw new Error('Not authenticated');
  }

  const response = await fetch(url, {
    ...options,
    headers: {
      ...options.headers,
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });
  handleUnauthorized(response);
  return response;
}

/**
 * Studio profile types
 */
export interface Studio {
  id: string;
  name: string;
  address_line1: string | null;
  address_line2: string | null;
  city: string | null;
  state: string | null;
  zip_code: string;
  phone: string | null;
  website: string | null;
  description: string | null;
  location: unknown | null;
  created_at: string;
  updated_at: string;
}

export interface UpdateStudioData {
  name?: string;
  address_line1?: string;
  address_line2?: string | null;
  city?: string;
  state?: string;
  zip_code?: string;
  phone?: string | null;
  website?: string | null;
  description?: string | null;
  /** Google Places locality place_id — set when the city was picked from the autocomplete. */
  google_place_id?: string;
}

/**
 * Contact types
 */
export interface Contact {
  id: string;
  studio_id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string | null;
  title: string | null;
  is_primary: boolean;
  receives_marketing_emails: boolean;
  receives_booking_emails: boolean;
  created_at: string;
  updated_at: string;
}

export interface CreateContactData {
  first_name: string;
  last_name: string;
  email: string;
  phone?: string | null;
  title?: string | null;
  is_primary?: boolean;
  receives_marketing_emails?: boolean;
  receives_booking_emails?: boolean;
}

export interface UpdateContactData {
  first_name?: string;
  last_name?: string;
  email?: string;
  phone?: string | null;
  title?: string | null;
  is_primary?: boolean;
  receives_marketing_emails?: boolean;
  receives_booking_emails?: boolean;
}

/**
 * Dashboard types
 */
export interface DashboardData {
  studio: Studio;
  upcoming_classes: UpcomingClass[];
  pending_actions: PendingAction[];
  recent_activity: ActivityItem[];
  stats: {
    total_bookings: number;
    upcoming_classes: number;
    pending_payments: number;
    completed_classes: number;
  };
}

export interface UpcomingClass {
  id: string;
  booking_id: string;
  show_name: string;
  class_name: string;
  scheduled_date: string;
  /** Raw TIME value from DB (e.g. '10:00:00'). Render via formatTimeDisplay from @sda/shared. */
  start_time: string;
  end_time: string | null;
  status: string;
  faculty_name: string | null;
  /** Where the class happens: the studio itself, or a hosted/custom venue — never the tour stop's theater. */
  venue_name: string;
  /** Class-location city/state (studio's, or parsed from a hosted venue's address). */
  city: string;
  state: string;
}

export interface PendingAction {
  type: 'payment_due' | 'confirmation_needed' | 'survey_pending' | 'document_required';
  /** Optional short heading; when present the card renders it bold with `message` as detail text. */
  title?: string;
  message: string;
  due_date: string | null;
  link: string;
  booking_id?: string;
}

export interface ActivityItem {
  id: string;
  type: string;
  message: string;
  timestamp: string;
  metadata?: Record<string, unknown>;
}

/**
 * Booking types
 */
export interface BookingListItem {
  id: string;
  status: string;
  booking_type: string;
  total_amount_cents: number;
  created_at: string;
  show: {
    id: string;
    name: string;
    logo_url: string | null;
  };
  /** Where the classes happen: the studio itself, or a hosted/custom venue — never the tour stop's theater. */
  venue: {
    name: string;
    city: string;
    state: string;
  };
  class_count: number;
  next_class_date: string | null;
}

export interface PaginatedResponse<T> {
  data: T[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    total_pages: number;
  };
}

/**
 * Payment types
 */
export interface Payment {
  id: string;
  booking_id: string;
  type: 'deposit' | 'balance' | 'refund';
  amount: number;
  status: 'pending' | 'succeeded' | 'failed';
  stripe_payment_intent_id: string | null;
  created_at: string;
}

/**
 * Booking details (extended booking with payments)
 */
export interface BookingDetails {
  id: string;
  studio_id: string;
  booking_number: string | null;
  status: 'pending' | 'confirmed' | 'completed' | 'cancelled';
  booking_type: 'direct' | 'hosted' | 'remote';
  show_name: string;
  show_logo_url: string | null;
  venue_name: string | null;
  venue_address: string | null;
  subtotal: number;
  travel_surcharge: number;
  marketing_fee: number;
  discount_cents: number;
  coupon_code: string | null;
  total_amount: number;
  deposit_amount: number;
  balance_amount: number;
  balance_status: 'pending' | 'deposit_paid' | 'paid';
  terms_accepted_at: string | null;
  terms_version: string | null;
  marketing_materials: boolean;
  marketing_materials_completed_at: string | null;
  marketing_materials_completed_by?: 'admin' | 'studio' | null;
  marketing_logo_url?: string | null;
  marketing_signup_method?: string | null;
  marketing_class_cost?: string | null;
  created_at: string;
  updated_at: string;
  classes: BookingClassDetail[];
  inquiries?: BookingInquirySummary[];
  payments: Payment[];
  /** Per-class auto-charge schedule; null unless classes are charged on different dates. */
  balance_schedule?: BalanceScheduleView | null;
}

/** One class's share of the balance and when it is auto-charged (15 days before the class). */
export interface BalanceScheduleRow {
  class_id: string;
  class_date: string;
  show_name: string | null;
  amount_cents: number;
  /** Already paid toward this class (an offline partial can make it partial). */
  paid_cents: number;
  /** YYYY-MM-DD; null for a cancelled class (never auto-charged). */
  charge_on: string | null;
  status: 'paid' | 'scheduled' | 'due' | 'cancelled';
  /** ISO timestamp of the auto-charge that paid it, when known. */
  paid_at: string | null;
}

export interface BalanceScheduleView {
  rows: BalanceScheduleRow[];
  /** Left to pay on non-cancelled classes. */
  remaining_cents: number;
  /** `retrying`: an overdue class (e.g. declined card) the cron retries every day. */
  next_charge: { date: string; amount_cents: number; retrying: boolean } | null;
}

/**
 * Booking class detail with faculty info
 */
export interface BookingClassDetail {
  id: string;
  class_option_name: string;
  class_date: string | null;
  time_slot: ClassTimeSlot | null;
  status: 'scheduled' | 'confirmed' | 'completed' | 'cancelled';
  participant_count: number | null;
  faculty_name: string | null;
  faculty_headshot_url: string | null;
  faculty_bio?: string | null;
}

// =============================================================================
// API Functions
// =============================================================================

/**
 * Get current studio profile
 */
export async function getStudioProfile(): Promise<Studio> {
  const response = await authenticatedFetch(`${API_BASE_URL}/api/studios/me`);
  const result = await response.json();

  if (!response.ok) {
    throw new Error(result.error?.message || 'Failed to get studio profile');
  }

  // API returns { success, data: { studio: {...} } }
  return result.data.studio;
}

/**
 * Update studio profile
 */
export async function updateStudioProfile(data: UpdateStudioData): Promise<Studio> {
  const response = await authenticatedFetch(`${API_BASE_URL}/api/studios/me`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
  const result = await response.json();

  if (!response.ok) {
    throw new Error(result.error?.message || 'Failed to update studio profile');
  }

  // API returns { success, data: { studio: {...} } }
  return result.data.studio;
}

/**
 * Get studio contacts
 */
export async function getContacts(): Promise<Contact[]> {
  const response = await authenticatedFetch(`${API_BASE_URL}/api/studios/me/contacts`);
  const result = await response.json();

  if (!response.ok) {
    throw new Error(result.error?.message || 'Failed to get contacts');
  }

  // API returns { success, data: { contacts: [...] } }
  return result.data.contacts;
}

/**
 * Create a new contact
 */
export async function createContact(data: CreateContactData): Promise<Contact> {
  const response = await authenticatedFetch(`${API_BASE_URL}/api/studios/me/contacts`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
  const result = await response.json();

  if (!response.ok) {
    throw new Error(result.error?.message || 'Failed to create contact');
  }

  // API returns { success, data: { contact: {...} } }
  return result.data.contact;
}

/**
 * Update a contact
 */
export async function updateContact(
  contactId: string,
  data: UpdateContactData
): Promise<Contact> {
  const response = await authenticatedFetch(
    `${API_BASE_URL}/api/studios/me/contacts/${contactId}`,
    {
      method: 'PUT',
      body: JSON.stringify(data),
    }
  );
  const result = await response.json();

  if (!response.ok) {
    throw new Error(result.error?.message || 'Failed to update contact');
  }

  // API returns { success, data: { contact: {...} } }
  return result.data.contact;
}

/**
 * Delete a contact
 */
export async function deleteContact(contactId: string): Promise<void> {
  const response = await authenticatedFetch(
    `${API_BASE_URL}/api/studios/me/contacts/${contactId}`,
    {
      method: 'DELETE',
    }
  );

  if (!response.ok) {
    const result = await response.json();
    throw new Error(result.error?.message || 'Failed to delete contact');
  }
}

/**
 * Get dashboard data
 */
export async function getDashboard(): Promise<DashboardData> {
  const response = await authenticatedFetch(`${API_BASE_URL}/api/studios/me/dashboard`);
  const result = await response.json();

  if (!response.ok) {
    throw new Error(result.error?.message || 'Failed to get dashboard data');
  }

  // Transform API response to match DashboardData interface
  const apiData = result.data;

  // API returns stats in different structure than frontend expects:
  // API: { stats: { upcoming_classes, pending_balance_cents, bookings: { total, pending, confirmed, completed }, active_inquiries } }
  // Frontend expects: { stats: { total_bookings, upcoming_classes, pending_payments, completed_classes } }
  const transformedData: DashboardData = {
    studio: apiData.studio,
    upcoming_classes: apiData.upcoming_classes || [],
    pending_actions: apiData.action_items || [], // API returns 'action_items'
    recent_activity: [], // API doesn't return this, frontend doesn't use it
    stats: {
      total_bookings: apiData.stats?.bookings?.total || 0,
      upcoming_classes: apiData.stats?.upcoming_classes || 0,
      pending_payments: (apiData.stats?.pending_balance_cents || 0) / 100, // Convert cents to dollars
      completed_classes: apiData.stats?.bookings?.completed || 0,
    },
  };

  return transformedData;
}

/**
 * Get bookings list with pagination and filtering
 */
export async function getBookings(params?: {
  page?: number;
  limit?: number;
  status?: string;
  show_id?: string;
}): Promise<PaginatedResponse<BookingListItem>> {
  const searchParams = new URLSearchParams();
  if (params?.page) searchParams.set('page', params.page.toString());
  if (params?.limit) searchParams.set('limit', params.limit.toString());
  if (params?.status) searchParams.set('status', params.status);
  if (params?.show_id) searchParams.set('show_id', params.show_id);

  const url = `${API_BASE_URL}/api/studios/me/bookings${
    searchParams.toString() ? `?${searchParams}` : ''
  }`;
  const response = await authenticatedFetch(url);
  const result = await response.json();

  if (!response.ok) {
    throw new Error(result.error?.message || 'Failed to get bookings');
  }

  // Transform backend response to match expected format
  // Backend returns: { success: true, data: { bookings: [...], pagination: {...} } }
  // Frontend expects: { data: [...], pagination: {...} }
  const bookingsRaw = result.data?.bookings || [];

  // Transform each booking to match BookingListItem interface
  const bookings: BookingListItem[] = bookingsRaw.map((booking: Record<string, unknown>) => {
    const classes = (booking.classes || []) as Array<Record<string, unknown>>;
    // Tour stop data is nested inside classes: classes[0].tour_stops
    const firstClass = classes[0] as Record<string, unknown> | undefined;
    const tourStop = firstClass?.tour_stops as Record<string, unknown> | null;
    const shows = tourStop?.shows as Record<string, unknown> | null;
    // Resolved class venue (studio or hosted/custom venue), computed by the backend
    const venue = booking.venue as Record<string, unknown> | null;

    // Determine the date to show in the list: prefer the next upcoming class,
    // otherwise fall back to the most recent past class so the list shows the
    // real date (matching the detail page) instead of "Date TBD". Compare as
    // date-only strings (YYYY-MM-DD sorts chronologically) to avoid the
    // UTC-midnight shift that `new Date('YYYY-MM-DD')` introduces.
    const now = new Date();
    const todayStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
    const classDates = classes
      .map((c) => c.class_date as string)
      .filter(Boolean)
      .sort();
    const nextClassDate =
      classDates.find((d) => d >= todayStr) ?? classDates[classDates.length - 1] ?? null;

    return {
      id: booking.id as string,
      status: booking.status as string,
      booking_type: 'direct', // Default to direct booking
      total_amount_cents: (booking.total_cents as number) || 0,
      created_at: booking.created_at as string,
      show: {
        id: (shows?.id as string) || '',
        name: (shows?.name as string) || 'Unknown Show',
        logo_url: (shows?.logo_url as string) || null,
      },
      venue: {
        name: (venue?.name as string) || 'Unknown Venue',
        city: (venue?.city as string) || '',
        state: (venue?.state as string) || '',
      },
      class_count: classes.length,
      next_class_date: nextClassDate,
    };
  });

  return {
    data: bookings,
    pagination: result.data?.pagination || { page: 1, limit: 10, total: 0, total_pages: 0 },
  };
}

/**
 * Get booking details
 */
export async function getBookingDetails(bookingId: string): Promise<BookingDetails> {
  const response = await authenticatedFetch(
    `${API_BASE_URL}/api/studios/me/bookings/${bookingId}`
  );
  const result = await response.json();

  if (!response.ok) {
    throw new Error(result.error?.message || 'Failed to get booking details');
  }

  return result.data;
}

/**
 * Cancel a pending booking
 */
export async function cancelBooking(bookingId: string): Promise<{ cancelled: boolean }> {
  const response = await authenticatedFetch(
    `${API_BASE_URL}/api/bookings/${bookingId}/cancel`,
    {
      method: 'POST',
    }
  );
  const result = await response.json();

  if (!response.ok) {
    throw new Error(result.error?.message || 'Failed to cancel booking');
  }

  return result.data;
}

/**
 * Pay remaining balance for a confirmed booking
 */
export async function payBookingBalance(bookingId: string): Promise<{ message: string; amount_cents: number }> {
  const response = await authenticatedFetch(
    `${API_BASE_URL}/api/bookings/${bookingId}/pay-balance`,
    {
      method: 'POST',
    }
  );
  const result = await response.json();

  if (!response.ok) {
    throw new Error(result.error?.message || 'Failed to process payment');
  }

  return result.data;
}

/**
 * Create a Stripe Checkout Session for balance payment
 */
export async function createBookingCheckoutSession(bookingId: string): Promise<{ checkoutUrl: string }> {
  const response = await authenticatedFetch(
    `${API_BASE_URL}/api/bookings/${bookingId}/create-checkout-session`,
    {
      method: 'POST',
    }
  );
  const result = await response.json();

  if (!response.ok) {
    throw new Error(result.error?.message || 'Failed to create checkout session');
  }

  return result.data;
}

/**
 * Create a Stripe Checkout Session for the deposit payment (manual/offline
 * bookings that owe a deposit). Mirrors createBookingCheckoutSession but hits
 * the deposit endpoint. The success redirect returns to the booking detail page
 * with checkout_success=true&session_id=..., which the shared verify handler
 * already processes.
 */
export async function createDepositCheckoutSession(bookingId: string): Promise<{ checkoutUrl: string }> {
  const response = await authenticatedFetch(
    `${API_BASE_URL}/api/bookings/${bookingId}/create-deposit-checkout-session`,
    {
      method: 'POST',
    }
  );
  const result = await response.json();

  if (!response.ok) {
    throw new Error(result.error?.message || 'Failed to create deposit checkout session');
  }

  return result.data;
}

/**
 * Verify a Stripe Checkout session after redirect back to the app
 */
export async function verifyBookingCheckout(bookingId: string, sessionId: string): Promise<{ status: 'paid' | 'pending' | 'failed' }> {
  const response = await authenticatedFetch(
    `${API_BASE_URL}/api/bookings/${bookingId}/verify-checkout`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ session_id: sessionId }),
    }
  );
  const result = await response.json();
  if (!response.ok) throw new Error(result.error?.message || 'Verification failed');
  return result.data;
}

/**
 * Mark marketing materials as complete for a booking
 */
export async function markMarketingMaterialsComplete(bookingId: string): Promise<void> {
  const response = await authenticatedFetch(
    `${API_BASE_URL}/api/bookings/${bookingId}/marketing-materials-complete`,
    {
      method: 'POST',
    }
  );

  if (!response.ok) {
    const result = await response.json();
    throw new Error(result.error?.message || 'Failed to mark marketing materials complete');
  }
}

/**
 * Submit marketing materials form data (logo, signup method, class cost)
 * Uses multipart form data for file upload support
 */
export async function submitMarketingMaterials(
  bookingId: string,
  data: { logo?: File; signupMethod: string; classCost: string }
): Promise<{ marketing_logo_url: string | null; marketing_signup_method: string; marketing_class_cost: string }> {
  const token = getAuthToken();
  if (!token) {
    throw new Error('Not authenticated');
  }

  const formData = new FormData();
  if (data.logo) {
    formData.append('logo', data.logo);
  }
  formData.append('signup_method', data.signupMethod);
  formData.append('class_cost', data.classCost);

  // Note: do NOT set Content-Type header — browser sets it with boundary for multipart
  const response = await fetch(
    `${API_BASE_URL}/api/bookings/${bookingId}/marketing-materials`,
    {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
      },
      body: formData,
    }
  );

  const result = await response.json();

  if (!response.ok) {
    throw new Error(result.error?.message || 'Failed to submit marketing materials');
  }

  return result.data;
}

// =============================================================================
// Utility Functions
// =============================================================================

/**
 * Format address as single line
 * Handles null/undefined values gracefully
 */
export function formatAddress(studio: Studio): string {
  // Check if we have the minimum required address fields
  if (!studio.address_line1 && !studio.city && !studio.state && !studio.zip_code) {
    return 'No address on file';
  }

  const parts: string[] = [];

  if (studio.address_line1) {
    parts.push(studio.address_line1);
  }

  if (studio.address_line2) {
    parts.push(studio.address_line2);
  }

  // Build city, state zip line only if we have at least one component
  const cityStateParts: string[] = [];
  if (studio.city) cityStateParts.push(studio.city);
  if (studio.state) {
    if (studio.city) {
      cityStateParts[cityStateParts.length - 1] += ',';
    }
    cityStateParts.push(studio.state);
  }
  if (studio.zip_code) cityStateParts.push(studio.zip_code);

  if (cityStateParts.length > 0) {
    parts.push(cityStateParts.join(' '));
  }

  return parts.join(', ') || 'No address on file';
}

/**
 * Get full name of a contact
 */
export function getContactFullName(contact: Contact): string {
  return `${contact.first_name} ${contact.last_name}`;
}

/**
 * Format booking status for display
 */
export function formatBookingStatus(status: string): string {
  const statusMap: Record<string, string> = {
    pending: 'Pending',
    confirmed: 'Confirmed',
    completed: 'Completed',
    cancelled: 'Cancelled',
  };
  return statusMap[status] || status;
}

/**
 * SDA-292: the card the studio's next payment will be charged to.
 */
export interface SavedCard {
  brand: string;
  last4: string;
  exp_month: number;
  exp_year: number;
}

export async function getSavedCard(): Promise<SavedCard | null> {
  const response = await authenticatedFetch(`${API_BASE_URL}/api/payments/card`);
  const result = await response.json();
  if (!response.ok) throw new Error(result.error?.message || 'Failed to load saved card');
  return result.data.card;
}

/**
 * Start a Stripe Checkout that saves a new card without charging it.
 * returnPath is where Stripe sends the studio back to (Profile or a booking).
 */
export async function createCardSetupSession(returnPath: string): Promise<{ checkoutUrl: string }> {
  const response = await authenticatedFetch(`${API_BASE_URL}/api/payments/card/setup-session`, {
    method: 'POST',
    body: JSON.stringify({ returnPath }),
  });
  const result = await response.json();
  if (!response.ok) throw new Error(result.error?.message || 'Failed to start the card update');
  return result.data;
}

/**
 * Finish a card update after Stripe redirects back with ?card_updated=true.
 */
export async function confirmCardUpdate(sessionId: string): Promise<SavedCard> {
  const response = await authenticatedFetch(`${API_BASE_URL}/api/payments/card/confirm`, {
    method: 'POST',
    body: JSON.stringify({ session_id: sessionId }),
  });
  const result = await response.json();
  if (!response.ok) throw new Error(result.error?.message || 'Failed to save the new card');
  return result.data.card;
}

/** "Visa ending 4242 · expires 08/27" */
export function describeSavedCard(card: SavedCard): string {
  const brand = card.brand ? card.brand.charAt(0).toUpperCase() + card.brand.slice(1) : 'Card';
  const expiry = `${String(card.exp_month).padStart(2, '0')}/${String(card.exp_year).slice(-2)}`;
  return `${brand} ending ${card.last4} · expires ${expiry}`;
}

/**
 * Get status badge color class
 */
export function getStatusBadgeClass(status: string): string {
  const colorMap: Record<string, string> = {
    pending: 'bg-warning-100 text-warning-700',
    confirmed: 'bg-success-100 text-success-700',
    completed: 'bg-primary-100 text-primary-700',
    cancelled: 'bg-danger-100 text-danger-700',
  };
  return colorMap[status] || 'bg-warm-100 text-warm-700';
}

/**
 * Alias for getStatusBadgeClass
 */
export const getStatusColor = getStatusBadgeClass;


/**
 * Calculate days until date
 */
export function daysUntil(dateString: string): number {
  const targetDate = dateString.split('T')[0]!;
  const today = new Date().toLocaleDateString('en-CA'); // YYYY-MM-DD in local timezone
  const target = new Date(targetDate + 'T00:00:00');
  const now = new Date(today + 'T00:00:00');
  const diffTime = target.getTime() - now.getTime();
  return Math.round(diffTime / (1000 * 60 * 60 * 24));
}
